import React, { useMemo, useCallback } from 'react';
import Box from '@mui/material/Box';
import { sp } from '@/theme/mui';

import { Checkbox } from '@/components/atoms/Checkbox/Checkbox';
import { BaseField, FieldProps } from '@/components/atoms/BaseField/BaseField';

export const BooleanTable = ({
  className,
  columns,
  rows,
  title,
  id,
  label,
  description,
  tooltip,
  isRequired,
  isHideTitle,
  value,
  onChange,
}: BooleanTableProps) => {
  const valueObject = useMemo(() => JSON.parse(value || '{}'), [value]);

  const handleCheckboxChange = useCallback(
    (row: string, column: string, value: boolean) => {
      const newValue = { ...valueObject };
      if (!newValue[row]) {
        newValue[row] = {};
      }
      newValue[row][column] = value;
      onChange?.(JSON.stringify(newValue));
    },
    [valueObject, onChange],
  );

  return (
    <BaseField
      id={id}
      label={label}
      description={description}
      tooltip={tooltip}
      isRequired={isRequired}
      isHideTitle={isHideTitle}
    >
      <Box
        component='table'
        sx={{ borderCollapse: 'collapse', borderSpacing: 0, width: '100%' }}
      >
        <thead>
          <tr>
            <Box
              component='th'
              sx={{
                pb: sp[12],
                typography: 'bodyMediumWeight',
                color: '#003413',
                textAlign: 'left',
              }}
            >
              {title}
            </Box>
            {columns.map((column) => (
              <Box
                component='th'
                key={column}
                sx={{
                  px: sp[12],
                  pb: sp[12],
                  typography: 'bodyMediumWeight',
                  color: '#000000',
                  minWidth: '100px',
                  textAlign: 'left',
                }}
              >
                {column}
              </Box>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <Box component='tr' key={row} sx={{ mt: sp[32], pt: sp[32] }}>
              <Box
                component='td'
                sx={{
                  py: sp[12],
                  pr: sp[12],
                  borderTop: '1px solid #cbc5b9',
                  typography: 'bodyMd',
                  color: '#404042',
                  textAlign: 'left',
                }}
              >
                {row}
              </Box>
              {columns.map((column) => (
                <Box
                  component='td'
                  key={`${row}-${column}`}
                  sx={{
                    py: sp[12],
                    borderTop: '1px solid #cbc5b9',
                    pl: sp[20],
                    '& > *': { width: 'min-content' },
                  }}
                >
                  <Checkbox
                    key={`${id}-${row}-${column}`}
                    id={`${id}-${row}-${column}`}
                    value={valueObject?.[row]?.[column]}
                    onChange={(value) =>
                      handleCheckboxChange(row, column, value)
                    }
                  />
                </Box>
              ))}
            </Box>
          ))}
        </tbody>
      </Box>
    </BaseField>
  );
};

export interface BooleanTableProps extends FieldProps<string> {
  className?: string;
  title?: string;
  columns: string[];
  rows: string[];
}

BooleanTable.displayName = 'BooleanTable';

export default BooleanTable;
